import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import {
    canPlanMigrateToNewPricing,
    checkIsNewProductKey,
    checkIsUnrestrictedProductKey,
    getMinimumSiteSubscriptionForFeatures,
    getSiteProductDefinition,
    isLegacySiteType,
    SITE_FEATURES_MAP,
} from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import { toast } from '@gitbook/spine-react';

import { useNavigation } from '../../navigation';
import { useSiteUpgradeModal } from '../../Screens/Site/components/Plan/useSiteUpgradeModal';
import { apiClient, apiQuery } from '../api';
import { useCurrentOrganization } from '../organization';
import { ContactSupportReasons, useContactSupport } from '../support';
import type { ContactSupportAction, SelfServeMigrateAction, SiteUpgradeAction } from './types';
import { isSiteUpgradeRequiredForFeature } from './useUpgradeUtils';

export type UseUpgradeSite = SiteUpgradeAction | ContactSupportAction | SelfServeMigrateAction;
/**
 * Return an action to upgrade a site. Returns undefined if no upgrade is required.
 */
export function useUpgradeSite(
    site: api.Site,
    featureKey: api.SiteFeatureKey
): UseUpgradeSite | null {
    const organization = useCurrentOrganization();
    const { navigate } = useNavigation();

    const requiredSiteType = getMinimumSiteSubscriptionForFeatures({
        isNewPricing: checkIsNewProductKey(organization.plan),
        featureKeys: [featureKey],
        defaultSiteType: site.type,
    });
    const sitePlan = getSiteProductDefinition(requiredSiteType ?? site.type);
    const isUpgradeRequired = isSiteUpgradeRequiredForFeature({
        organization,
        site,
        featureKey,
    });
    const upgradeModal = useSiteUpgradeModal();
    const contactSupport = useContactSupport();
    const feature = SITE_FEATURES_MAP[featureKey];
    const isLegacySite = isLegacySiteType(site.type);

    // Check whether this is a self-serve migrate CTA.
    const isSelfServeMigrateCTA =
        isUpgradeRequired && canPlanMigrateToNewPricing(organization.plan);

    // Check whether this is a contact support CTA for legacy sites. Which means:
    //  - Site type is legacy
    //  - Upgrade is required
    //  - We could not determine what site type to upgrade to
    const isContactSupportCTA = isUpgradeRequired && isLegacySite && !requiredSiteType;
    const canPreviewSitePlanUpdate = !isLegacySite && isUpgradeRequired && requiredSiteType;

    const { data: preview } = useSuspenseQuery(
        apiQuery(
            'previewSitePlanUpdate',
            canPreviewSitePlanUpdate
                ? { organizationId: organization.id, siteId: site.id, plan: requiredSiteType }
                : skipToken,
            { nullOnError: true }
        )
    );

    return React.useMemo(() => {
        // If user `canPreviewSitePlanUpdate` && preview is `null` then API returned an error
        // and we don't want to show any upgrade CTA.
        if (canPreviewSitePlanUpdate && preview === null) {
            return null;
        }

        // No CTA if an upgrade is not required.
        if (!isUpgradeRequired) {
            return null;
        }

        if (isSelfServeMigrateCTA) {
            return {
                _type: 'self-serve-migrate',
                label: 'Upgrade to new pricing',
                tooltip: {
                    label: 'This feature requires a site plan. Upgrade to our latest pricing to gain access to it.',
                },
                kind: 'upgrade',
                leadingIcon: Icon.Upgrade,
                onPress: async () => {
                    navigate({
                        overlay: {
                            type: 'org_settings',
                            organization: organization.id,
                            tab: { type: 'upgrade' },
                            hash: featureKey,
                        },
                    });
                },
            };
        }

        if (isContactSupportCTA) {
            return {
                _type: 'contact-support',
                plan: `${sitePlan.title}`,
                label: 'Get access',
                tooltip: {
                    label: 'This feature requires a site plan. Contact support to learn more.',
                },
                kind: 'secondary',
                leadingIcon: Icon.Send,
                onPress: async () =>
                    contactSupport({
                        reason: ContactSupportReasons.REQUEST_FEATURE,
                        subject: `I'm interested in ${feature.title}`,
                    }),
            };
        }

        const buttonProps: Omit<SiteUpgradeAction, 'label'> = {
            _type: 'site-upgrade',
            plan: sitePlan.title,
            kind: 'upgrade' as const,
            leadingIcon: Icon.Upgrade,
            onPress: async () => {
                // Skip the upgrade modal for unrestricted orgs (Enterprise, Community, Internal)
                if (checkIsUnrestrictedProductKey(organization.plan)) {
                    await toast.catch(async () => {
                        await apiClient.sites.updateSitePlan(organization.id, site.id, {
                            plan: sitePlan.key,
                            // Pass the featureKey as reason, so an intent will be created and processed
                            reason: featureKey,
                        });

                        toast.success(`Your site was switched to ${sitePlan.title}.`);
                    }, 'Error while updating site plan');
                    return;
                }
                upgradeModal.open(site, { tab: 'plans', reason: featureKey });
            },
        };

        /* No checkout or trial required, we only require switching site types */
        if (preview?.type === 'go-ahead') {
            return {
                ...buttonProps,
                label: `Switch to ${sitePlan.title}`,
                kind: 'primary',
                leadingIcon: undefined,
            };
        }

        /* The available trial needs to be started */
        if (preview?.type === 'trial-available') {
            return {
                ...buttonProps,
                label: 'Start a 14-day trial',
            };
        }

        return {
            ...buttonProps,
            label: `Upgrade to ${sitePlan.title}`,
        };
    }, [
        featureKey,
        isUpgradeRequired,
        sitePlan,
        upgradeModal,
        contactSupport,
        isContactSupportCTA,
        isSelfServeMigrateCTA,
        feature.title,
        preview?.type,
        organization.id,
        organization.plan,
        site,
        navigate,
        preview,
        canPreviewSitePlanUpdate,
    ]);
}
